export enum ElementType {
  TEXT_INPUT = "text_input",
  PHOTO_PICKER = "photo_picker",
  IMAGE_SWATCH = "image_swatch",
  DROPDOWN = "dropdown",
  VARIANT_SELECTOR = "variant_selector",
  DATE_PICKER = "date_picker",
  TIME_PICKER = "time_picker",
  AUDIO_INPUT = "audio_input",
  CROSSWORD = "crossword",
  WORD_SEARCH = "word_search",
  SPOTIFY = "spotify",
  ELEMENT_GENERATOR = "element_generator",
}

export enum AudioType {
  AUDIO_UPLOAD = "audio_upload",
  AUDIO_RECORDER = "audio_recorder",
}

export enum FunctionType {
  InputText = "input_text",
  PickPhoto = "pick_photo",
  PickClipArt = "pick_clip_art",
  ShowLayout = "show_layout",
  ChangeTemplate = "change_template",
  ChangeTextColor = "change_text_color",
  ChangeImageFilter = "change_image_filter",
  ChangeTextStrokeColor = "change_text_stroker_color",
  GenerateCalendarImage = "generate_calendar_image",
  GenerateCalendarText = "generate_calendar_text",
  GenerateClockImage = "generate_clock_image",
  ChangeCalendarStyle = "change_calendar_style",
  GenerateQRCode = "generate_qr_code",
  ChangeQRCodeStyle = "change_qr_code_style",
  GenerateCrossword = "generate_crossword",
  GenerateWordSearch = "generate_word_search",
  GenerateMoonPhase = "generate_moon_phase",
  GenerateElementByChar = "generate_element_by_char",
  InputCharByIndex = "input_char_by_index",
  ConditionalLayoutByTextLength = "conditional_layout_by_text_length",
}

export interface OutputMappingRule {
  sourceField: string;
  targetElementId: string;
  mappingValues?: Record<string, any>;
}

export interface Element {
  modifyStatus?: "deleted" | "added" | "updated" | "unchanged";
  _id?: string;
  id?: string;
  zIndex: number;
  isHidden?: boolean;
  isDisabled?: boolean;
  type: ElementType;
  label: string;
  helpText?: string;
  modalHelpText?: string;
  variableName?: string;
  typeConfig?: TypeConfig;
  validationRules?: ValidationRule;
  values?: ElementValue[];
  functions: ElementFunction[];
  outputMappings?: OutputMappingRule[];
  visibilityRule?: VisibilityRule | null;
  output?: ElementOutput;
  functionOutputs: FunctionOutput[];
  elementId?: string;
  title?: string;
  showDivider?: boolean;
  libraryBinding?: LibraryBinding | null;
}

export type TypeConfig =
  | { type: "text_input"; config: TextInputConfig }
  | { type: "photo_picker"; config: PhotoPickerConfig }
  | { type: "image_swatch"; config: ImageSwatchConfig }
  | { type: "dropdown"; config: object }
  | { type: "variant_selector"; config: object & VariantSelectorConfig }
  | { type: "date_picker"; config: DatePickerConfig }
  | { type: "time_picker"; config: object }
  | { type: "audio_input"; config: AudioInputConfig }
  | { type: "crossword"; config: CrosswordConfig }
  | { type: "word_search"; config: WordSearchConfig }
  | { type: "spotify"; config: object }
  | { type: "element_generator"; config: Record<string, unknown> };

export interface ValidationRule {
  isRequired: boolean;
  minLength?: number;
  maxLength?: number;
  maxLines?: number;
  maxCharsPerLine?: number;
}

export interface TextInputConfig {
  placeholder?: string;
  isTextarea?: boolean;
  defaultValue?: string;
}

export interface CrosswordConfig {
  placeholder?: string;
  defaultValue?: string;
}

export interface WordSearchConfig {
  placeholder?: string;
  defaultValue?: string;
}

export interface DatePickerConfig {
  displayFormats?: string[];
}


export enum ProcessingStepType {
  ApplyAIFilter = "apply_ai_filter",
}

export type ProcessingStep = {
  type: ProcessingStepType;
  options?: { filterId?: string };
};

export interface PhotoPickerConfig {
  enableCutFace: boolean;
  enableRemoveBg: boolean;
  enableAIGenerator: boolean;
  enableMultiStepProcessing?: boolean;
  serviceProvider: string;
  AIGeneratorPrompt: string;
  processingSteps?: ProcessingStep[];
  multiUploadEnabled?: boolean;
  minUploadCount?: number | null;
  maxUploadCount?: number | null;
  aiCombineEnabled?: boolean;
}

export interface AudioInputConfig {
  isAudioUpload: boolean;
  isAudioRecorder: boolean;
}

export interface ImageSwatchConfig {
  enableShowValue: boolean;
}

export interface VariantSelectorConfig {
  variantId?: string;
}

export interface LibraryBinding {
  resourceType: "clip_art";
  categoryId: string;
}

export interface ElementValue {
  zIndex: number;
  value: string;
  label?: string;
  tooltip?: string;
  thumbnailPath?: string;
  color?: string;
  selected?: boolean;
  id?: string;
  optionId?: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  libraryItemId?: string | null;
}

export interface GeneratorOptionValue {
  id: string;
  value: string;
  label: string;
  zIndex: number;
  selected: boolean;
  optionId: string;
}

export interface GeneratorCharConfig {
  label: string;
  typeConfig: TypeConfig;
  values: GeneratorOptionValue[];
}

export interface GeneratorAction {
  type: "pick_clip_art";
  targetType: "layer";
  targetIds: string[];
}

export interface LengthRule {
  minLength: number;
  maxLength: number;
  targetGroupId: string;
}

export interface ElementFunction {
  id: string;
  type: FunctionType;
  targetLayerId?: string;
  targetIds?: string[];
  values?: FunctionValue[];
  sourceElementId?: string;
  generatedType?: string;
  configs?: Record<string, GeneratorCharConfig>;
  actions?: GeneratorAction[];
  lengthRules?: LengthRule[];
}

export interface FunctionValue {
  zIndex: number;
  value: string;
  mappingValue: string;
}

export const VisibilityActions = {
  SHOW: "show",
  HIDE: "hide",
} as const;

export type VisibilityAction =
  (typeof VisibilityActions)[keyof typeof VisibilityActions];

export const Operators = {
  EQUALS: "equals",
  IN: "in",
  INDEX_EQUALS: "index_equals",
  LENGTH_EQUALS: "length_equals",
} as const;

export type Operator = (typeof Operators)[keyof typeof Operators];

export type IndexEqualsValue = [number, string];
export type LengthEqualsValue = number;

export const LogicOperators = {
  AND: "AND",
  OR: "OR",
} as const;

export type LogicOperator =
  (typeof LogicOperators)[keyof typeof LogicOperators];

export interface SingleCondition {
  elementId: string;
  operator: Operator;
  expectedValue?: unknown;
}

export interface ConditionNode {
  logic: LogicOperator;
  conditions: (SingleCondition | ConditionNode)[];
}

export interface VisibilityRule {
  action: VisibilityAction;
  condition: ConditionNode;
}

export interface ElementOutputBase {
  elementId: string;
  label: string;
}

export type PhotoPickerValue = {
  originalUrl: string;
  filteredUrl: string;
  thumbnailUrl?: string | null;
  previewUrl?: string | null;
  position: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  transform: {
    scaleX: number;
    scaleY: number;
    angle: number;
    skewX: number;
    skewY: number;
    flipX: boolean;
    flipY: boolean;
    relativeLeftRatio: number;
    relativeTopRatio: number;
    relativeScaleX: number;
    relativeScaleY: number;
    relativeAngle: number;
  };
  meta?: {
    name?: string;
    fileSize?: number;
    mimeType?: string;
  };
};

export type ElementOutput =
  | (ElementOutputBase & { type: "text_input"; value: string })
  | (ElementOutputBase & {
      type: "photo_picker";
      value: PhotoPickerValue;
    })
  | (ElementOutputBase & { type: "image_swatch"; value: string })
  | (ElementOutputBase & { type: "dropdown"; value: string | string[] })
  | (ElementOutputBase & { type: "variant_selector"; value: string })
  | (ElementOutputBase & { type: "qr_code"; value: string });

export interface FunctionOutput {
  functionType: FunctionType;
  targetLayerId?: string;
  mappingValue?: string;
}

export interface ElementOutputWithFunctions {
  elementId: string;
  output: ElementOutput;
  functionOutputs: FunctionOutput[];
}

type PhotoType = "face_cutout" | "remove_bg";

export interface LinkedCampaign {
  _id: string;
  title: string;
  productSlug: string;
  store?: string;
}

export default interface CustomizationForm {
  id: string;
  _id: string;
  name: string;
  userId?: string;
  store?: string;
  hasPhotoPicker: boolean;
  photoType: PhotoType;
  isAIImageGen?: boolean;
  elements: Element[];
  elementsCount?: number;
  createdAt: string;
  updatedAt: string;
  campaignId?: string | null;
  isVariantForm?: boolean;
  campaigns?: LinkedCampaign[];
}
